// Launcher-tile normalization — the ONE canonical guard that keeps the apps
// launcher from ever rendering a blank tile.
//
// A launcher tile's contract is { title, caption, icon, iconBg, route } (every
// manifest-v2 app in workspace/apps/* ships that shape), but tiles reach the
// launcher from THREE composers that historically emitted thinner rows:
//   • loadWorkspaceApps' extension branch pushed { label, path } refs,
//   • a Mongo `settings.apps` row can be a bare { route } pointer,
//   • an app manifest can simply omit icon/iconBg.
// SearchAppTile reads `s.title` / `s.icon` / `s.iconBg` / `s.route`, so every
// one of those thin rows rendered as a nameless tile with an empty sprite
// glyph (Svg emits `<use href="#">` for an unresolvable name — the natali
// blank-tiles bug, 2026-07-30).
//
// `normalizeLauncherTile` is applied at the single launcher feed
// (searchAppGroupsFor) so BOTH read paths — the raw takeover project config
// and the composed workspaceSettings.apps — pass through it, and
// `fillLauncherTileFrom` lets the launcher composer (_appendAppsLauncher)
// backfill a thin stored row from the richer installed-app manifest tile so
// real meta always beats the generic fallback.

export const LAUNCHER_FALLBACK_ICON = 'blocks'

// The iconBg names SearchAppsMenu's TILE_GRADIENTS understands — the fallback
// picker below must never emit a name outside this set.
export const LAUNCHER_ICON_BGS = ['blue', 'green', 'red', 'purple', 'orange', 'cerulean']

// '/x/sales-analysis-july-25' → 'Sales Analysis July 25'. Last path segment,
// kebab/snake → Title Case. Query/hash stripped. '' for an empty route.
export const titleFromRoute = (route) => {
  const path = String(route || '')
    .split('?')[0]
    .split('#')[0]
  const seg = path.split('/').filter(Boolean).pop() || ''
  const words = seg.replace(/[-_]+/g, ' ').trim()
  if (!words) return ''
  return words
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ')
}

// Deterministic iconBg pick — hashed off the tile identity so re-renders and
// re-composes never shuffle a fallback tile's color.
const _bgFor = (seed) => {
  const s = String(seed || '')
  let hash = 0
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0
  return LAUNCHER_ICON_BGS[Math.abs(hash) % LAUNCHER_ICON_BGS.length]
}

// Normalize ONE tile to the full launcher contract. Returns a NEW object
// (never mutates state rows); null for rows with no identity at all.
export const normalizeLauncherTile = (tile) => {
  if (!tile || typeof tile !== 'object') return null
  const route = tile.route || tile.path || ''
  const title = tile.title || tile.label || titleFromRoute(route)
  if (!route && !title) return null
  const out = { ...tile }
  if (route) out.route = route
  if (title) out.title = title
  if (!out.icon) out.icon = LAUNCHER_FALLBACK_ICON
  if (!out.iconBg) out.iconBg = _bgFor(route || title)
  return out
}

// Backfill MISSING display meta on `base` from a richer duplicate (`rich`) —
// used by the launcher composer when a stored settings row and an installed
// app's manifest tile share the same route: the stored row keeps its slot and
// any deliberate overrides, the manifest fills the gaps. Returns a NEW object
// when anything was filled; `base` unchanged otherwise.
export const fillLauncherTileFrom = (base, rich) => {
  if (!base || typeof base !== 'object' || !rich || typeof rich !== 'object') return base
  const patch = {}
  if (!(base.route || base.path) && (rich.route || rich.path)) {
    patch.route = rich.route || rich.path
  }
  if (!(base.title || base.label) && (rich.title || rich.label)) {
    patch.title = rich.title || rich.label
  }
  // IDENTITY, not display meta — but backfilled for exactly the same reason.
  // `appKey` is what lets the tile resolve the installed app's real store name
  // (workspaceConfigs.js `_appRowsWithKey`); a thin stored row that carries
  // only `{ route }` would otherwise keep printing the app manifest's English
  // literal while the store shows the app's translated name.
  if (!base.appKey && rich.appKey) patch.appKey = rich.appKey
  if (!base.caption && rich.caption) patch.caption = rich.caption
  if (!base.icon && rich.icon) patch.icon = rich.icon
  if (!base.iconBg && rich.iconBg) patch.iconBg = rich.iconBg
  // the module's own glyph for the launcher (dropdowns spec 2026-09-30 §3.3)
  if (!base.glyph && rich.glyph) patch.glyph = rich.glyph
  return Object.keys(patch).length ? { ...base, ...patch } : base
}
